import { ref } from 'vue';
import { ConfigProvider } from 'ant-design-vue';

import Consts from '@/api/Consts';

import eftLogo from '../assets/themes/eft.webp';


type UiThemeConfig = {
	primaryColor: string;
	hoverColor: string;
	linkColor: string;
	logo: string;
	favicon: string;
	captionKey: string | null;
};

export const UI_THEMES = {
	EFT: {
		primaryColor: '#EF7F1A',
		hoverColor: '#AC5B13',
		linkColor: '#CB6C16',
		logo: eftLogo,
		favicon: '/static/themes/eft_32.ico',
		captionKey: 'ui-theme.eft.caption',
	},
} as const satisfies Record<string, UiThemeConfig>;

export type UiThemeKey = keyof typeof UI_THEMES;

export type UiTheme = typeof UI_THEMES[UiThemeKey];


const BODY_THEME_CLASS_PREFIX = 'ui-theme-';
const CUSTOM_LINKS_CLASS = 'ui-theme-custom-links';
const DEFAULT_FAVICON_HREF = '/static/favicon.ico';


const readBootstrappedThemeLabel = (): string => {
	if (typeof window === 'undefined') {
		return '';
	}
	return window.__UI_THEME__ ?? '';
};


const resolveThemeFromLabel = (label: string): UiTheme | null => {
	if (!label || !(label in UI_THEMES)) {
		return null;
	}
	return UI_THEMES[label as UiThemeKey];
};


const clearBodyThemeClasses = (): void => {
	if (typeof document === 'undefined') {
		return;
	}
	const { classList } = document.body;
	[...classList].forEach(className => {
		if (className.startsWith(BODY_THEME_CLASS_PREFIX)) {
			classList.remove(className);
		}
	});
};


const applyBodyThemeClass = (label: string): void => {
	if (typeof document === 'undefined') {
		return;
	}
	clearBodyThemeClasses();
	document.body.classList.add(BODY_THEME_CLASS_PREFIX + label.toLowerCase());
	document.body.classList.add(CUSTOM_LINKS_CLASS);
};


const applyFavicon = (href: string): void => {
	if (typeof document === 'undefined') {
		return;
	}
	let link = document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]')
		?? document.querySelector<HTMLLinkElement>('link[rel="icon"]');
	if (!link) {
		link = document.createElement('link');
		link.rel = 'shortcut icon';
		link.type = 'image/x-icon';
		document.head.appendChild(link);
	}
	link.href = href;
};


const applyThemeCssVars = (theme: UiTheme): void => {
	if (typeof document === 'undefined') {
		return;
	}
	const root = document.documentElement;
	root.style.setProperty('--ant-primary-color', theme.primaryColor);
	root.style.setProperty('--ant-primary-color-hover', theme.hoverColor);
	root.style.setProperty('--ant-primary-color-active', theme.hoverColor);
	root.style.setProperty('--custom-link-color', theme.linkColor);
	root.style.setProperty('--custom-link-color-hover', theme.hoverColor);
	root.style.setProperty('--custom-link-color-active', theme.hoverColor);
};


const clearThemeCssVars = (): void => {
	if (typeof document === 'undefined') {
		return;
	}
	const root = document.documentElement;
	root.style.removeProperty('--ant-primary-color');
	root.style.removeProperty('--ant-primary-color-hover');
	root.style.removeProperty('--ant-primary-color-active');
	root.style.removeProperty('--custom-link-color');
	root.style.removeProperty('--custom-link-color-hover');
	root.style.removeProperty('--custom-link-color-active');
	clearBodyThemeClasses();
};


const initialTheme = resolveThemeFromLabel(readBootstrappedThemeLabel());


export const appliedPrimaryColor = ref<string>(initialTheme?.primaryColor ?? Consts.COLORS.primary0);
export const appliedThemeLogo = ref<string | null>(initialTheme?.logo ?? null);
export const appliedThemeCaptionKey = ref<string | null>(initialTheme?.captionKey ?? null);


export const resolveUiTheme = (label: string): UiTheme | null => {
	return resolveThemeFromLabel(label);
};


export const applyUiTheme = (label: string): void => {
	const theme = resolveThemeFromLabel(label);
	if (theme) {
		appliedPrimaryColor.value = theme.primaryColor;
		appliedThemeLogo.value = theme.logo;
		appliedThemeCaptionKey.value = theme.captionKey;
		ConfigProvider.config({
			theme: {
				primaryColor: theme.primaryColor,
			},
		});
		applyBodyThemeClass(label);
		applyThemeCssVars(theme);
		applyFavicon(theme.favicon);
		return;
	}
	appliedPrimaryColor.value = Consts.COLORS.primary0;
	appliedThemeLogo.value = null;
	appliedThemeCaptionKey.value = null;
	ConfigProvider.config({
		theme: {
			primaryColor: Consts.COLORS.primary0,
		},
	});
	clearThemeCssVars();
	applyFavicon(DEFAULT_FAVICON_HREF);
};
